/**
 * The personal "Available for chat" switch (spec 7.2, 12.1): in the sidebar
 * footer, the mobile account menu and Support → Chat. Available means the
 * switch is on, this console is open and its tab was visible in the last
 * three minutes; customers then see "We're online". Browser sessions only.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Switch } from "@/components/kit/switch";
import { supportChatAvailabilityQuery } from "@/lib/queries";
import { setChatAvailability } from "@/lib/support-chat";
import { inboxWriteFailureMessage } from "@/lib/support-inbox";
import { cn } from "@/lib/utils";
import { Failure, useIsSessionAdmin } from "@/routes/support/shared";

import { askForChatNotifications, ensureAudio } from "./chat-hub";

export function ChatAvailabilityToggle({
  className,
  showStatus = false,
}: {
  className?: string;
  /** Under the switch: whether I count as available right now. */
  showStatus?: boolean;
}) {
  const isSession = useIsSessionAdmin();
  const client = useQueryClient();
  const query = useQuery({
    ...supportChatAvailabilityQuery(),
    enabled: isSession,
  });
  const save = useMutation({
    mutationFn: setChatAvailability,
    onSuccess: (view) =>
      client.setQueryData(supportChatAvailabilityQuery().queryKey, view),
  });
  if (!isSession) return null;
  const toggle = save.isPending ? save.variables : (query.data?.toggle ?? true);
  return (
    <div
      data-testid="chat-availability"
      className={cn("flex flex-col gap-1", className)}
    >
      <Switch
        label="Available for chat"
        checked={toggle}
        disabled={query.isPending || save.isPending}
        onCheckedChange={(on) => {
          if (on) {
            ensureAudio();
            askForChatNotifications();
          }
          save.mutate(on);
        }}
      />
      {showStatus && query.data ? (
        <p
          data-testid="chat-availability-status"
          className="text-compact text-muted-foreground"
        >
          {query.data.available
            ? "You count as available: customers see that the team is online."
            : toggle
              ? "Keep this console open in a visible tab to count as available."
              : "You are away. Chats still reach the inbox; customers see the away message."}
        </p>
      ) : null}
      {query.isError ? (
        <Failure message="Chat availability could not be read. Reload the page to try again." />
      ) : null}
      {save.isError ? (
        <Failure message={inboxWriteFailureMessage(save.error)} />
      ) : null}
    </div>
  );
}
